import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteAgentMemoryEntry } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { AgentMemoryEntry } from '@/types'
import { Trash2, X } from 'lucide-react'
import { toast } from 'sonner'

interface DeleteMemoryDialogProps {
  projectId: string
  entry: Pick<AgentMemoryEntry, 'id' | 'agentId'>
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteMemoryDialog({ projectId, entry, open, onOpenChange }: DeleteMemoryDialogProps) {
  const deleteMemory = useDeleteAgentMemoryEntry(projectId, entry.agentId)

  const handleDelete = async () => {
    try {
      await deleteMemory.mutateAsync(entry.id)
      toast.success('Memory entry deleted')
      onOpenChange(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to delete memory entry')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold text-foreground">Delete Memory?</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm leading-[1.6] text-foreground">
          Are you sure you want to delete this memory? This action cannot be undone and the memory will be permanently removed.
        </DialogDescription>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="h-10 rounded-lg px-4 font-bold">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMemory.isPending}
            className="h-10 rounded-lg px-4 font-bold text-white"
          >
            <Trash2 className="h-5 w-5" />
            {deleteMemory.isPending ? 'Deleting...' : 'Delete Memory'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
